import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { healthScoreColor, healthScoreLabel } from '../../utils/financialHealth';

const SIZE = 96;
const STROKE = 8;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function HealthScoreRing({ score, t }) {
  const color = healthScoreColor(score);
  const label = healthScoreLabel(score, t);
  const progress = Math.min(score, 100) / 100;
  const offset = CIRCUMFERENCE * (1 - progress);

  const tooltipContent = [
    `${t('budgetPlanner.dashboard.savingsRate', 'Savings rate')}: 30%`,
    `${t('budgetPlanner.dashboard.housingLabel', 'Housing')}: 25%`,
    `${t('budgetPlanner.dashboard.debtWeight', 'Deuda')}: 20%`,
    `${t('budgetPlanner.goals.emergencyFund', 'Emergency fund')}: 15%`,
    `${t('budgetPlanner.dashboard.incomeDiversity', 'Diversidad de ingresos')}: 10%`,
  ].join(' | ');

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box
        sx={{
          position: 'relative',
          width: SIZE,
          height: SIZE,
          mx: 'auto',
          cursor: 'help',
          transition: 'transform 0.2s ease',
          '&:hover': { transform: 'scale(1.05)' },
        }}
      >
        <svg width={SIZE} height={SIZE} style={{ transform: 'rotate(-90deg)' }}>
          {/* Background track */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="#E8E8E8"
            strokeWidth={STROKE}
          />
          {/* Progress arc */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={color}
            strokeWidth={STROKE}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.8s ease-out, stroke 0.3s ease',
            }}
          />
        </svg>
        {/* Center text */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: SIZE,
            height: SIZE,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color, lineHeight: 1, fontSize: '1.4rem', fontFeatureSettings: "'tnum'" }}
          >
            {score}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.2, mt: 0.25 }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
    <Tooltip title={tooltipContent} arrow placement="bottom">
      <IconButton
        size="small"
        aria-label={t('budgetPlanner.dashboard.healthInfo', 'Información sobre la puntuación')}
        sx={{
          position: 'absolute',
          top: -2,
          right: -6,
          p: 0.25,
          color: '#9CA3AF',
          '&:hover': { color: '#1E3A5F' },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
    </Box>
  );
}
